import { useEffect, useRef, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useScrollReveal } from "../components/useScrollReveal";
import { useSEO } from "../components/useSEO";
import modusLogo from "../Modus_logo.png";
import "./Home.css";

const AUTOPLAY_MS = 5200;

/**
 * Home page. Beyond the standard header/reveal/count-up/parallax
 * (already familiar from the previous version), this page now has
 * three real, independently-timed auto-playing systems converted
 * faithfully from the original vanilla JS:
 *
 *   1. The "Verify with Sophia-AI" loop — cursor glides in, clicks the
 *      button, shows a loading state, then a verified state, holds,
 *      resets. Runs forever unless paused.
 *   2. The mock dashboard's practice-patterns/NPD-details tab
 *      auto-switcher — shares the same pause control as #1, since in
 *      the original both are one "live demo" concept.
 *   3. The feature carousel — autoplay with hover/touch/manual-nav
 *      pausing, resumes after 3.5s, and only runs while the carousel
 *      is actually visible on screen.
 *
 * Each keeps its own react-appropriate simplification of the original
 * DOM-manipulation approach (e.g. real state instead of classList
 * toggling, effect dependencies instead of manually-tracked timer
 * variables) while preserving the same observable timing and pausing
 * behavior a visitor would actually notice.
 */
export default function Home() {
  useScrollReveal(0.15);
  useSEO(
    "Modus | Physician Quality, Measured by Outcomes",
    "Modus rates physicians on real patient outcomes, not billing codes. Provider data management (PDM) for workers' comp, payers, and employers."
  );

  // ---- Verify-with-Sophia-AI loop + dashboard tab auto-switcher ----
  // These share one pause control in the original design (one "live
  // demo" toggle in the dashboard topbar), so they share `playing` here.
  const [playing, setPlaying] = useState(true);
  // Dashboard score ring: draws itself and shifts color as it counts
  // up, using the same real tier thresholds the actual product uses
  // (>=60 good/green, 30-59 caution/amber, <30 concern/red) — so the
  // animation itself teaches how the scoring works, not just decorates.
  const [ringScore, setRingScore] = useState(0);
  const RING_TARGET = 77;

  function getScoreColor(score) {
    if (score >= 60) return "var(--green)";
    if (score >= 30) return "var(--amber)";
    return "var(--red)";
  }
  const [verifyState, setVerifyState] = useState("idle"); // idle | loading | verified
  const [cursorKey, setCursorKey] = useState(0);
  const [activeTab, setActiveTab] = useState("practice");
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  const handleTabClick = (name) => setActiveTab(name);

  useEffect(() => {
    const reducedMotion = !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    if (reducedMotion) {
      setRingScore(RING_TARGET);
      return;
    }
    const dur = 1400;
    const start = performance.now();
    let rafId;
    function tick(now) {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setRingScore(RING_TARGET * eased);
      if (p < 1) rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    const reducedMotion = !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    if (reducedMotion) {
      setVerifyState("verified");
      return;
    }
    if (!playing || !isPageVisible) return;

    let timers = [];
    function cycle() {
      setVerifyState("idle");
      timers.push(setTimeout(() => setCursorKey((k) => k + 1), 250));
      timers.push(
        setTimeout(() => {
          setVerifyState("loading");
          timers.push(
            setTimeout(() => {
              setVerifyState("verified");
              timers.push(setTimeout(cycle, 4200));
            }, 900)
          );
        }, 1100)
      );
    }
    cycle();

    return () => timers.forEach(clearTimeout);
  }, [playing, isPageVisible]);

  useEffect(() => {
    const reducedMotion = !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    if (reducedMotion || !playing || !isPageVisible) return;

    const delay = activeTab === "practice" ? 7200 : 6200;
    const timer = setTimeout(() => {
      setActiveTab((prev) => (prev === "practice" ? "npd" : "practice"));
    }, delay);

    return () => clearTimeout(timer);
  }, [playing, isPageVisible, activeTab]);

  // ---- Count-up numbers ----
  useEffect(() => {
    function countUp(el) {
      const target = parseFloat(el.getAttribute("data-count"));
      const prefix = el.getAttribute("data-prefix") || "";
      const suffix = el.getAttribute("data-suffix") || "";
      const isInt = Number.isInteger(target);
      const dur = 1400;
      const start = performance.now();
      function tick(now) {
        const p = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = target * eased;
        el.textContent =
          prefix + (isInt ? Math.round(val).toLocaleString() : val.toFixed(1)) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    const countEls = document.querySelectorAll("[data-count]");
    const countIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            countUp(e.target);
            countIo.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    countEls.forEach((el) => countIo.observe(el));
    return () => countIo.disconnect();
  }, []);

  // ---- Hero dashboard mouse parallax + scroll-tilt ----
  // Extends the existing mouse-tilt with a second, scroll-driven axis:
  // as the hero scrolls past, the dashboard subtly rotates in 3D space
  // as if settling back into the page, rather than just fading away.
  // Same element, same rAF loop, same reduced-motion gate — this is
  // additive to what already existed, not a separate new system.
  useEffect(() => {
    let rafId;
    let cleanup = () => {};
    const stage = document.getElementById("dashParallax");
    const heroEl = document.querySelector(".hero");
    if (
      stage &&
      heroEl &&
      matchMedia("(prefers-reduced-motion: no-preference)").matches &&
      matchMedia("(pointer: fine)").matches
    ) {
      let tx = 0, ty = 0, cx = 0, cy = 0;
      let targetRotate = 0, currentRotate = 0;

      const handleMouseMove = (e) => {
        const r = stage.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 18;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 18;
      };
      const handleMouseLeave = () => { tx = 0; ty = 0; };

      const handleScroll = () => {
        const heroRect = heroEl.getBoundingClientRect();
        if (!heroRect.height) return; // not laid out yet — avoid a divide-by-zero poisoning the eased value with NaN
        // 0 while the hero is fully in view at the top; increases as
        // the user scrolls past it, capped at a subtle 10deg max —
        // this is meant to read as settling depth, not a spin.
        const scrolledPast = Math.max(0, -heroRect.top);
        const progress = Math.min(1, scrolledPast / (heroRect.height * 0.8));
        targetRotate = progress * 10;
      };

      heroEl.addEventListener("mousemove", handleMouseMove);
      heroEl.addEventListener("mouseleave", handleMouseLeave);
      window.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();

      function raf() {
        cx += (tx - cx) * 0.06;
        cy += (ty - cy) * 0.06;
        currentRotate += (targetRotate - currentRotate) * 0.08;
        stage.style.transform =
          `translate(${cx.toFixed(2)}px, ${cy.toFixed(2)}px) rotateX(${currentRotate.toFixed(2)}deg)`;
        rafId = requestAnimationFrame(raf);
      }
      raf();
      cleanup = () => {
        heroEl.removeEventListener("mousemove", handleMouseMove);
        heroEl.removeEventListener("mouseleave", handleMouseLeave);
        window.removeEventListener("scroll", handleScroll);
        if (rafId) cancelAnimationFrame(rafId);
      };
    }
    return cleanup;
  }, []);

  // ---- Feature carousel ----
  const trackRef = useRef(null);
  const carouselRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [fillingDot, setFillingDot] = useState(false);
  const [carouselInView, setCarouselInView] = useState(false);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const resumeTimerRef = useRef(null);

  const goToSlide = (i) => {
    const clamped = (i + 8) % 8;
    const track = trackRef.current;
    if (!track) return;
    const slideEl = track.children[clamped];
    if (slideEl) track.scrollTo({ left: slideEl.offsetLeft, behavior: "smooth" });
    setActiveSlide(clamped);
  };

  const pauseThenResume = () => {
    setCarouselPaused(true);
    setFillingDot(false);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setCarouselPaused(false), 3500);
  };

  const handleDotClick = (i) => { goToSlide(i); pauseThenResume(); };
  const handleArrowClick = (dir) => { goToSlide(activeSlide + dir); pauseThenResume(); };

  // sync active dot with manual scroll/swipe
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        let best = 0, bestDist = Infinity;
        Array.from(track.children).forEach((s, i) => {
          const dist = Math.abs(s.offsetLeft - track.scrollLeft);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        setActiveSlide(best);
        ticking = false;
      });
    };
    track.addEventListener("scroll", onScroll);
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  // only autoplay while the carousel is actually visible
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setCarouselInView(e.isIntersecting)),
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const reducedMotion = !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    if (reducedMotion || !carouselInView || carouselPaused) {
      setFillingDot(false);
      return;
    }
    setFillingDot(true);
    const interval = setInterval(() => {
      setActiveSlide((prev) => {
        const next = (prev + 1) % 8;
        const track = trackRef.current;
        if (track) {
          const slideEl = track.children[next];
          if (slideEl) track.scrollTo({ left: slideEl.offsetLeft, behavior: "smooth" });
        }
        return next;
      });
      // restart the fill animation for the new active dot
      setFillingDot(false);
      requestAnimationFrame(() => setFillingDot(true));
    }, AUTOPLAY_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselInView, carouselPaused]);

  return (
    <>
      <SiteHeader />



<section className="hero">
  <div className="hero-ribbon">
    <span className="hero-ribbon-icon">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c.3 3.5 1.1 5.9 2.5 7.3C15.9 10.7 18.3 11.5 21.8 11.8c-3.5.3-5.9 1.1-7.3 2.5-1.4 1.4-2.2 3.8-2.5 7.3-.3-3.5-1.1-5.9-2.5-7.3C8.1 12.9 5.7 12.1 2.2 11.8c3.5-.3 5.9-1.1 7.3-2.5C10.9 7.9 11.7 5.5 12 2z"/></svg>
    </span>
    <span className="hero-ribbon-label">First to integrate</span>
    <span className="hero-ribbon-sep">/</span>
    <div className="hero-ribbon-track">
      <div className="hero-ribbon-marquee">
        <span className="hero-ribbon-msg">CMS's new National Provider Directory</span>
        <span className="hero-ribbon-dot">.</span>
        <span className="hero-ribbon-msg">Real-time WC patient appointment availability with our Sophia AI</span>
        <span className="hero-ribbon-dot">.</span>
        <span className="hero-ribbon-msg" aria-hidden="true">CMS's new National Provider Directory</span>
        <span className="hero-ribbon-dot" aria-hidden="true">.</span>
        <span className="hero-ribbon-msg" aria-hidden="true">Real-time WC patient appointment availability with our Sophia AI</span>
        <span className="hero-ribbon-dot" aria-hidden="true">.</span>
      </div>
    </div>
  </div>
  <div className="hero-glow glow-a"></div>
  <div className="hero-glow glow-b"></div>
  <div className="hero-grid"></div>
  <div className="wrap">
    <div className="hero-inner">
      <div>
        <h1 className="hero-title">AI-Powered Provider Data Management to Find the <span className="accent">Right Doctor</span> in Your Network</h1>
        <h2 className="hero-pdm-subhead">Provider data management (PDM) for workers' comp, payers, and employers</h2>
        <p className="hero-pdm-intro">Modus PDM verifies each physician's details and whether they're accepting referrals, then scores them on real patient outcomes from 183M+ Medicare patient records.</p>
        <a href="/platform" className="hero-pdm-cta">See the Modus PDM Platform &rarr;</a>
        <p className="hero-sub">Automated AI verification of WC plan acceptance and appointment availability, so you always place patients with a data-backed, outcomes-driven doctor.</p>
        <div className="hero-ctas" style={{marginTop: "12px"}}>
          <button className="btn btn-teal btn-lg" onClick={() => (window.location.href = "/about#contact")}>Book a Demo</button>
          <button className="btn btn-outline btn-lg" onClick={() => (window.location.href = "/platform#scoring")}>See the Platform</button>
        </div>
      </div>

      <div className="reveal hero-visual-stage">
      <div id="dashParallax">
      <div className="dash-frame" id="dashFrame">
        <div className="dash-topbar">
          <img src={modusLogo} alt="Modus" className="dash-topbar-logo" />
          <span className="dash-live"><span className="dash-live-dot"></span>1 live</span>
          <button className="dash-live-toggle" type="button" aria-label={playing ? "Pause animation" : "Play animation"} aria-pressed={String(!playing)} onClick={() => setPlaying((p) => !p)}>
            {playing ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4l13 8-13 8V4z"/></svg>
            )}
          </button>
        </div>
        <div className="dash-body">

          <div className="dash-panel">
            <div className="dash-sec dash-identity">
              <div className="dash-gauge">
                <svg width="46" height="46" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="27" fill="none" stroke="var(--page)" strokeWidth="6"/>
                  <circle cx="32" cy="32" r="27" fill="none" stroke={getScoreColor(ringScore)} strokeWidth="6" strokeLinecap="round" strokeDasharray="169.6" strokeDashoffset={169.6 * (1 - ringScore / 100)}/>
                </svg>
                <div className="dash-gauge-val" style={{color: getScoreColor(ringScore)}}>{Math.round(ringScore)}</div>
              </div>
              <div>
                <div className="dash-name">Elena Ruiz, MD</div>
                <span className="dash-spec">Orthopedic Surgery</span>
              </div>
            </div>
            <div className="dash-sec" style={{position: "relative"}}>
              <button className={`dash-btn dash-btn-primary verify-btn ${verifyState === "verified" ? "done" : ""}`} type="button">
                <span className="verify-btn-icon">
                  <svg className={verifyState === "loading" ? "spin" : ""} width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c.3 3.5 1.1 5.9 2.5 7.3C15.9 10.7 18.3 11.5 21.8 11.8c-3.5.3-5.9 1.1-7.3 2.5-1.4 1.4-2.2 3.8-2.5 7.3-.3-3.5-1.1-5.9-2.5-7.3C8.1 12.9 5.7 12.1 2.2 11.8c3.5-.3 5.9-1.1 7.3-2.5C10.9 7.9 11.7 5.5 12 2z"/></svg>
                </span>
                <span className="verify-btn-label">
                  {verifyState === "loading" ? "Verifying with Sophia-AI…" : verifyState === "verified" ? "Verified by Sophia-AI" : "Verify with Sophia-AI"}
                </span>
              </button>
              {cursorKey > 0 && (
                <div key={cursorKey} className="fake-cursor animate" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M4 2 L4 18.5 L8.3 14.8 L11.2 21.3 L14.1 20 L11.3 13.7 L17 13.6 Z" fill="#1a2333" stroke="#ffffff" strokeWidth="1.3" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
            <div className="dash-sec">
              <div className={`verify-results ${verifyState === "verified" ? "shown" : ""}`}>
                <div className="verify-item">
                  <span className="verify-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
                  <div><div className="verify-item-title">In your network</div></div>
                </div>
                <div className="verify-item">
                  <span className="verify-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
                  <div><div className="verify-item-title">Accepts Workers' Comp</div><div className="verify-item-meta">Last verified 9/17/2026</div></div>
                </div>
                <div className="verify-item">
                  <span className="verify-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
                  <div><div className="verify-item-title">Appointment availability in next 3 weeks</div><div className="verify-item-meta">Last verified 9/17/2026</div></div>
                </div>
                <div className="verify-item">
                  <span className="verify-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
                  <div><div className="verify-item-title">No background issues</div></div>
                </div>
              </div>
            </div>
          </div>

          <div className="dash-right">
            <div className="dash-tabbar">
              <span className={`dash-tabbtn ${activeTab === "practice" ? "active" : ""}`} onClick={() => handleTabClick("practice")}>Practice patterns</span>
              <span className={`dash-tabbtn ${activeTab === "npd" ? "active" : ""}`} onClick={() => handleTabClick("npd")}>NPD details</span>
            </div>
            <div className="dash-tab-content">
            <div className={`dgrid tab-panel ${activeTab === "practice" ? "active" : "hidden"}`}>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Cost/Pt</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">15% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">$342</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "74.2%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers $401</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">All Opioid Use</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">56% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">18%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "38.2%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers 41%</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Surgeries/New Pt</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">90% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">5%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "8.9%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers 49%</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Joint Scopes/Pt</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">67% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">2%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "29%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers 6%</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Spine Fusion/Pt</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">100% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">0%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "2%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers 2%</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Tests/Pt</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">100% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">0%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "2%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers 1%</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Repeated Surgery</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge flat">Same</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">4%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill flat" style={{width: "50%"}}></div><div className="mcard-bar-tick" style={{left: "50%"}}></div></div>
                <div className="mcard-peer center">Peers 4%</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Hospitalization</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">50% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">1%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "43.5%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers 2%</div>
              </div>

              <div className="dgrid-card">
                <div className="mcard-head2">
                  <div className="mcard-head-left"><span className="mcard-title">Mortality</span><svg className="mcard-info" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></div>
                  <span className="mcard-badge good">100% Less</span>
                </div>
                <div className="mcard-eyebrow">This provider</div>
                <div className="mcard-value2">0%</div>
                <div className="mcard-bar-track"><div className="mcard-bar-fill good" style={{width: "2%"}}></div><div className="mcard-bar-tick" style={{left: "87%"}}></div></div>
                <div className="mcard-peer">Peers 1%</div>
              </div>

            </div>

            <div className={`npd-panel tab-panel ${activeTab === "npd" ? "active" : "hidden"}`}>
              <div className="npd-src">Sourced from CMS NPD</div>
              <div className="npd-grid">

                <div className="npd-card">
                  <div className="npd-card-title">Personal &amp; legal</div>
                  <div className="npd-row"><span className="npd-row-label">NPI</span><span className="npd-row-val">1922384756</span></div>
                  <div className="npd-row"><span className="npd-row-label">Credential</span><span className="npd-row-val">Doctor of Medicine</span></div>
                  <div className="npd-row"><span className="npd-row-label">Practicing since</span><span className="npd-row-val">2011</span></div>
                  <div className="npd-row"><span className="npd-row-label">Specialty</span><span className="npd-row-val">Orthopaedic Surgery</span></div>
                  <div className="npd-row"><span className="npd-row-label">State license</span><span className="npd-row-val">TX · #R48210</span></div>
                  <div className="npd-badge good"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Currently practicing</div>
                </div>

                <div className="npd-card">
                  <div className="npd-card-title">Location &amp; contact</div>
                  <div className="npd-map">
                    <svg className="npd-map-pin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z"/><circle cx="12" cy="10" r="2.4"/></svg>
                  </div>
                  <div className="npd-addr">1400 University Ave<br />Austin, TX</div>
                  <div className="npd-phone">(512) 555-0142</div>
                  <div className="npd-row" style={{marginTop: "5px"}}><span className="npd-row-label">Practice groups</span><span className="npd-row-val">2 active</span></div>
                </div>

                <div className="npd-card">
                  <div className="npd-card-title">Insurance &amp; connectivity</div>
                  <div className="npd-row"><span className="npd-row-label">Medicare</span><span className="npd-row-val">Accepted</span></div>
                  <div className="npd-badge good"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Insurance verified</div>
                  <div className="npd-note"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>No digital connectivity on file yet</div>
                </div>

                <div className="npd-card">
                  <div className="npd-card-title">Providers at this location</div>
                  <div className="npd-colleagues">
                    <span className="npd-colleague">RM</span><span className="npd-colleague">WE</span><span className="npd-colleague">SM</span><span className="npd-colleague">SL</span><span className="npd-colleague">RJ</span><span className="npd-colleague">DK</span><span className="npd-colleague">CR</span><span className="npd-colleague">AK</span><span className="npd-colleague">CM</span><span className="npd-colleague">RD</span>
                  </div>
                  <div className="npd-row" style={{marginTop: "6px"}}><span className="npd-row-label">Active colleagues</span><span className="npd-row-val">10</span></div>
                </div>

              </div>
            </div>
            </div>
          </div>

        </div>
      </div>
      </div>
      </div>
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <div className="section-head reveal wide">
      <p className="kicker">Before a referral goes out</p>
      <h2 className="section-title">Who's <span className="accent-teal">the right doctor?</span></h2>
      <p className="section-lead">Five checks, run on every provider before a name ever reaches your team.</p>
    </div>
    <div className="criteria-grid reveal-stagger">
      <div className="criteria-card">
        <div className="criteria-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z"/><circle cx="12" cy="10" r="2.4"/></svg></div>
        <h3>In-network, accepts WC</h3>
        <p>Confirmed in your network and actively accepting Workers' Comp, not just listed as participating.</p>
      </div>
      <div className="criteria-card">
        <div className="criteria-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg></div>
        <h3>Validated address &amp; contact</h3>
        <p>Address and phone verified, not a stale entry pulled from a directory nobody's checked in years.</p>
      </div>
      <div className="criteria-card">
        <div className="criteria-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
        <h3>Available within 4 weeks</h3>
        <p>Confirmed to be taking new patients with an appointment inside the next four weeks.</p>
      </div>
      <div className="criteria-card">
        <div className="criteria-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg></div>
        <h3>Statistically valid score</h3>
        <p>A quality and cost score built on enough claims volume to be meaningful, not stretched thin over a handful of cases.</p>
      </div>
      <div className="criteria-card">
        <div className="criteria-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg></div>
        <h3>Clean background check</h3>
        <p>No open exclusions, sanctions, or red flags, verified on demand and on a set schedule, not just at credentialing.</p>
      </div>
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <div className="section-head reveal wide">
      <p className="kicker">Your network, augmented</p>
      <h2 className="section-title network-title">Bring the network you already have. <span className="accent-teal">We make it intelligent.</span></h2>
      <p className="section-lead">You don't rebuild a panel from scratch. Load your PPO, Workers' Comp, or existing provider network as-is; Modus cleans it, maps it to the NPI registry, and layers our scoring and AI on top.</p>
    </div>

    <div className="layer-flow reveal-stagger-3d">
      <div className="layer-step">
        <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></div>
        <div className="layer-step-label">Step 1</div>
        <h3>Load your network</h3>
        <p>PPO, Workers' Comp, or any list you already have.</p>
        <div className="layer-mini mini-import">
          <div className="layer-skel-row"><div className="layer-skel-bar" style={{width: "82%"}}></div></div>
          <div className="layer-skel-row"><div className="layer-skel-bar" style={{width: "58%"}}></div></div>
          <div className="layer-skel-row"><div className="layer-skel-bar" style={{width: "70%"}}></div></div>
        </div>
      </div>
      <div className="layer-connector"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg><span className="flow-dot"></span></div>
      <div className="layer-step">
        <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg></div>
        <div className="layer-step-label">Step 2</div>
        <h3>Clean &amp; map to NPI</h3>
        <p>Deduplicated, standardized, and matched to NPI.</p>
        <div className="layer-mini">
          <div className="layer-skel-row done"><div className="layer-skel-bar" style={{width: "52%"}}></div><span className="layer-skel-tag">NPI <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" style={{display: "inline", verticalAlign: "-1px"}}><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="layer-skel-row done"><div className="layer-skel-bar" style={{width: "38%"}}></div><span className="layer-skel-tag">NPI <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" style={{display: "inline", verticalAlign: "-1px"}}><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="layer-skel-row done"><div className="layer-skel-bar" style={{width: "46%"}}></div><span className="layer-skel-tag">NPI <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" style={{display: "inline", verticalAlign: "-1px"}}><polyline points="20 6 9 17 4 12"/></svg></span></div>
        </div>
      </div>
      <div className="layer-connector"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg><span className="flow-dot"></span></div>
      <div className="layer-step">
        <div className="layer-step-top">
          <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z"/></svg></div>
          <span className="layer-badge">Industry first</span>
        </div>
        <div className="layer-step-label">Step 3</div>
        <h3>Layer the Modus rating</h3>
        <p>Cost, opioid prescribing, and complications, scored on every provider.</p>
        <div className="layer-mini mini-score">
          <div className="mini-score-caps">
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg>AI legal background check</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="3" y1="12" x2="21" y2="12"/></svg>AI WC plan acceptance check</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><line x1="16" y1="3" x2="16" y2="7"/><line x1="8" y1="3" x2="8" y2="7"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Realtime AI Appointment availability check</div>
          </div>
        </div>
      </div>
      <div className="layer-connector"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg><span className="flow-dot"></span></div>
      <div className="layer-step">
        <div className="layer-step-top">
          <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg></div>
          <span className="layer-badge">Industry first</span>
        </div>
        <div className="layer-step-label">Step 4</div>
        <h3>NPD layer</h3>
        <p>CMS's National Provider Directory, layered in automatically.</p>
        <div className="layer-mini">
          <div className="mini-score-caps">
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z"/><circle cx="12" cy="10" r="2.4"/></svg>Contacts &amp; address verification</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg>Active license</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 17H7a4 4 0 0 1 0-8h2"/><path d="M15 7h2a4 4 0 0 1 0 8h-2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Affiliations &amp; associations</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21V9l8-5 8 5v12"/><line x1="9" y1="21" x2="9" y2="13"/><line x1="15" y1="21" x2="15" y2="13"/></svg>Practice ownership</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>Insurance acceptance</div>
          </div>
        </div>
      </div>
      <div className="layer-connector"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg><span className="flow-dot"></span></div>
      <div className="layer-step">
        <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></div>
        <div className="layer-step-label">Step 5</div>
        <h3>Use it anywhere</h3>
        <p>Your augmented network, wherever your team works.</p>
        <div className="layer-mini mini-channels-list">
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>Open API</div>
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="6" rx="1.5"/><rect x="2" y="15" width="20" height="6" rx="1.5"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg></span>MCP Server</div>
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-3.8 7 8.5 8.5 0 0 1-9.8.9L3 21l1.6-4.4a8.5 8.5 0 0 1 .9-9.8 8.38 8.38 0 0 1 7-3.8h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg></span>MS Teams / Slack / Chatbot</div>
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg></span>Modus UI</div>
        </div>
      </div>
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <div className="section-head reveal wide">
      <p className="kicker">The output</p>
      <h2 className="section-title">What comes out <span className="accent-teal">the other side.</span></h2>
      <p className="section-lead">Every network we augment gets the same treatment. Here's what typically comes back.</p>
    </div>
    <div className="results-band reveal">
      <div className="report-list">
        <div className="report-row">
          <div className="report-num">47%</div>
          <div className="report-text">
            <div className="report-label">Confirmed accepting Workers' Comp</div>
            <div className="report-sub">Validated directly through Sophia AI. Human verification is also available as a service for anything AI can't confirm.</div>
          </div>
          <div className="report-bar"><div className="report-bar-fill" style={{ "--pct": "47%" }}></div></div>
        </div>
        <div className="report-row">
          <div className="report-num">96%</div>
          <div className="report-text">
            <div className="report-label">Address &amp; contact verified</div>
          </div>
          <div className="report-bar"><div className="report-bar-fill" style={{ "--pct": "96%" }}></div></div>
        </div>
        <div className="report-row">
          <div className="report-num">44%</div>
          <div className="report-text">
            <div className="report-label">Highly rated Modus doctors available in your network</div>
          </div>
          <div className="report-bar"><div className="report-bar-fill" style={{ "--pct": "44%" }}></div></div>
        </div>
        <div className="report-row">
          <div className="report-num">62%</div>
          <div className="report-text">
            <div className="report-label">Accepting new patients within 4 weeks</div>
          </div>
          <div className="report-bar"><div className="report-bar-fill" style={{ "--pct": "62%" }}></div></div>
        </div>
        <div className="report-row">
          <div className="report-num warn">5%</div>
          <div className="report-text">
            <div className="report-label">Flagged on background check</div>
          </div>
          <div className="report-bar"><div className="report-bar-fill warn" style={{ "--pct": "5%" }}></div></div>
        </div>
      </div>
      <p className="results-note">Sample analysis of a real Midwest Workers' Comp payer network, 2,000 doctors, run through the Modus pipeline.</p>
    </div>
  </div>
</section>

<section style={{background: "var(--page)"}}>
  <div className="wrap">
    <div className="section-head reveal wide">
      <p className="kicker">Why intelligence matters</p>
      <h2 className="section-title network-title">An intelligent layer <span className="accent-teal">changes what your network can do.</span></h2>
      <p className="section-lead">This isn't a cleaner directory. It's a scoring, routing, and audit layer that changes cost, experience, and risk, all from the same data you already have.</p>
    </div>

    <div className="feat-carousel reveal" ref={carouselRef} onMouseEnter={() => setCarouselPaused(true)} onMouseLeave={() => setCarouselPaused(false)} onTouchStart={pauseThenResume}>
      <button className="feat-arrow feat-prev" type="button" aria-label="Previous" onClick={() => handleArrowClick(-1)}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>
      <button className="feat-arrow feat-next" type="button" aria-label="Next" onClick={() => handleArrowClick(1)}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></button>

      <div className="feat-track" ref={trackRef}>

        <div className="feat-slide bt-teal">
          <div className="feat-text">
            <div className="feat-eyebrow">01 · Medicare-scale data</div>
            <h3 className="feat-title">Scoring built on the toughest population</h3>
            <p className="feat-cap">A doctor who does right by older, sicker Medicare patients does right by everyone.</p>
            <p className="feat-detail">Modus scores are trained on real claims outcomes, not self-reported specialties or star ratings, so the signal holds across every payer and plan you route through.</p>
          </div>
          <div className="feat-visual">
            <div><div className="feat-bignum">183M+</div><div className="feat-bignum-lbl">historical patient records</div></div>
          </div>
        </div>

        <div className="feat-slide bt-green">
          <div className="feat-text">
            <div className="feat-eyebrow">02 · Better outcomes</div>
            <h3 className="feat-title">Better outcomes at lower cost</h3>
            <p className="feat-cap">Route to the right doctor on the first visit instead of the second or third.</p>
            <p className="feat-detail">Every misrouted visit adds a duplicate workup, a delayed diagnosis, and a second claim. Getting it right the first time is where the savings actually come from.</p>
          </div>
          <div className="feat-visual">
            <div className="bviz-bars">
              <div className="bviz-bar-col"><div className="bviz-bar short"></div><span className="bviz-bar-cap">Right doctor<br />first visit</span></div>
              <div className="bviz-bar-col"><div className="bviz-bar tall"></div><span className="bviz-bar-cap">Wrong doctor<br />first visit</span></div>
            </div>
          </div>
        </div>

        <div className="feat-slide bt-teal">
          <div className="feat-text">
            <div className="feat-eyebrow">03 · Patient experience</div>
            <h3 className="feat-title">Built on who actually accepts WC patients</h3>
            <p className="feat-cap">A patient experience built on providers confirmed to be taking patients right now.</p>
            <p className="feat-detail">Directories go stale the moment they're published. Modus re-checks acceptance and availability continuously, so a referral never lands on a closed door.</p>
          </div>
          <div className="feat-visual">
            <div className="bviz-route">
              <div className="bviz-doc-row declined"><span className="bviz-doc-avatar">DR</span><span className="bviz-doc-txt">Dr. Reyes, no longer takes WC</span><span className="bviz-doc-status"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span></div>
              <div className="bviz-doc-row confirmed"><span className="bviz-doc-avatar">DA</span><span className="bviz-doc-txt">Dr. Alvarez, accepting WC now</span><span className="bviz-doc-status"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
            </div>
          </div>
        </div>

        <div className="feat-slide bt-plain">
          <div className="feat-text">
            <div className="feat-eyebrow">04 · Operations</div>
            <h3 className="feat-title">Less administrative burden</h3>
            <p className="feat-cap">Real-time WC, availability, and contact checks, with referral letters sent automatically.</p>
            <p className="feat-detail">What used to be a phone call and a fax is now a background check that runs before your coordinator even opens the case.</p>
          </div>
          <div className="feat-visual">
            <div className="feat-icon-badge"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6a1 1 0 0 1 1 1v1h2a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2V4a1 1 0 0 1 1-1Z"/><polyline points="9 13 11 15 15 10.5"/></svg></div>
          </div>
        </div>

        <div className="feat-slide bt-amber">
          <div className="feat-text">
            <div className="feat-eyebrow">05 · Implementation</div>
            <h3 className="feat-title">Lowest IT involvement in the industry</h3>
            <p className="feat-cap">Stand it up without pulling in your engineering team.</p>
            <p className="feat-detail">Load your existing network file and go. No data warehouse project, no schema migration, no six-month integration timeline.</p>
          </div>
          <div className="feat-visual">
            <div className="feat-icon-badge"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z"/></svg></div>
          </div>
        </div>

        <div className="feat-slide bt-plain">
          <div className="feat-text">
            <div className="feat-eyebrow">06 · Data quality</div>
            <h3 className="feat-title">Legacy data, cleaned up</h3>
            <p className="feat-cap">Cleansing, updating, and verification, no rip-and-replace.</p>
            <p className="feat-detail">Duplicates merged, addresses standardized, and every record matched to NPI, on top of the network file you already maintain today.</p>
          </div>
          <div className="feat-visual">
            <div className="feat-icon-badge"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 3 21 9 15 9"/></svg></div>
          </div>
        </div>

        <div className="feat-slide bt-red">
          <div className="feat-text">
            <div className="feat-eyebrow">07 · Risk</div>
            <h3 className="feat-title">Legal background check to lower your risk</h3>
            <p className="feat-cap">On-demand and scheduled legal checks, not a once-a-year audit.</p>
            <p className="feat-detail">Run it on demand or put it on a set schedule, your call. Either way, a legal issue surfaces then and there, instead of waiting for the next yearly audit to find out.</p>
          </div>
          <div className="feat-visual">
            <div className="bviz-ring">
              <div className="bviz-ring-wrap">
                <svg width="100" height="100" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="40" fill="none" stroke="var(--surface)" strokeWidth="9"/>
                  <circle cx="48" cy="48" r="40" fill="none" stroke="var(--green)" strokeWidth="9" strokeLinecap="round" strokeDasharray="251" strokeDashoffset="15"/>
                </svg>
                <div className="bviz-ring-val"><b>94%</b><span>Clean</span></div>
              </div>
              <div className="bviz-ring-legend">
                <div className="bviz-ring-leg"><span className="bviz-ring-dot" style={{background: "var(--green)"}}></span>Verified &amp; in good standing</div>
                <div className="bviz-ring-leg"><span className="bviz-ring-dot" style={{background: "var(--red)"}}></span>Flagged, auto-reviewed</div>
              </div>
            </div>
          </div>
        </div>

        <div className="feat-slide bt-teal">
          <div className="feat-text">
            <div className="feat-eyebrow">08 · Team knowledge</div>
            <h3 className="feat-title">Institutional memory that's retained with your team</h3>
            <p className="feat-cap">Notes and outcomes from every past referral, kept with the provider, not one person's inbox.</p>
            <p className="feat-detail">When a coordinator moves on, what they learned about a provider doesn't leave with them. It's attached to the provider record for good.</p>
          </div>
          <div className="feat-visual">
            <div className="bviz-feed">
              <div className="bviz-note"><span className="bviz-note-avatar">JK</span><div><div className="bviz-note-who">J. Kim, Care Coordinator</div><div className="bviz-note-txt">"Dr. Alvarez, same-day availability, great with anxious patients."</div></div></div>
              <div className="bviz-note"><span className="bviz-note-avatar">MT</span><div><div className="bviz-note-who">M. Torres, Nurse Navigator</div><div className="bviz-note-txt">"Confirmed accepting new WC referrals as of this week."</div></div></div>
            </div>
          </div>
        </div>

      </div>

      <div className="feat-dots">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <button
            key={i}
            type="button"
            className={`feat-dot ${activeSlide === i ? "active" : ""} ${activeSlide === i && fillingDot ? "filling" : ""}`}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => handleDotClick(i)}
          >
            <span
              className="feat-dot-fill"
              style={
                activeSlide === i && fillingDot
                  ? { transition: `width ${AUTOPLAY_MS}ms linear`, width: "100%" }
                  : { transition: "none", width: "0%" }
              }
            />
          </button>
        ))}
      </div>
      <div className="feat-slide-count"><span>{activeSlide + 1}</span> / <span>8</span></div>
    </div>
  </div>
</section>


<section>
  <div className="wrap">
    <div className="closing reveal">
      <div className="closing-inner">
        <h2>See what your directory is missing.</h2>
        <p>See how a provider's real practice patterns stack up against peers, or talk to us about what a Modus rollout looks like for your network.</p>
        <div className="closing-ctas">
          <button className="btn btn-white btn-lg" onClick={() => (window.location.href = "/platform#scoring")}>See the Platform</button>
          <button className="btn btn-outline btn-lg" onClick={() => (window.location.href = "/about#contact")}>Book a Demo</button>
        </div>
      </div>
    </div>
  </div>
</section>


      <SiteFooter />
    </>
  );
}